import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Star, MapPin, ShieldCheck, Crown, Clock, Globe, Heart, Briefcase, CheckCircle2, Loader2, MessageSquare, Gauge, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BadgeRow, AchievementsGrid } from "@/components/AchievementBadges";
import DbsStatusBlock from "@/components/DbsStatusPill";

export default function CleanerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [fav, setFav] = useState(false);
  const [priceOpts, setPriceOpts] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get(`/cleaners/${id}`).then((r) => setC(r.data)).catch(() => setC(false));
    api.get(`/cleaners/${id}/pricing`).then((r) => setPriceOpts(r.data.services || {})).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => {});
  }, [id]);

  const toggleFav = async () => {
    if (!user) return navigate("/login");
    try {
      if (fav) { await api.delete(`/favourites/${id}`); setFav(false); toast.success("Removed from favourites"); }
      else { await api.post(`/favourites/${id}`); setFav(true); toast.success("Saved to favourites"); }
    } catch { toast.error("Please log in"); }
  };

  if (c === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  if (c === false) return <div className="min-h-screen bg-background"><Header /><div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Cleaner not found.</div></div>;

  const stats = [
    { label: "Completed jobs", value: c.completed_jobs },
    { label: "Reliability", value: c.reliability_score != null ? `${c.reliability_score}%` : "—" },
    { label: "Response rate", value: `${c.response_rate}%` },
    { label: "Avg. response", value: `${c.avg_response_minutes}m` },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="relative h-52 sm:h-64 bg-primary">
        <img src={c.gallery?.[0] || c.photo_url} alt="" className="h-full w-full object-cover opacity-40" />
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="relative -mt-20 grid lg:grid-cols-[1fr_360px] gap-8">
          {/* MAIN */}
          <div>
            <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-6">
                <img src={c.photo_url} alt={c.name} className="h-28 w-28 rounded-2xl object-cover border-4 border-card shadow-lg" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold">{c.name}</h1>
                    {c.premium && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent text-white text-xs font-semibold"><Crown className="h-3.5 w-3.5" /> Premium</span>}
                  </div>
                  <p className="text-muted-foreground flex items-center gap-2 mt-1">
                    <MapPin className="h-4 w-4" /> {c.town}
                  </p>
                  <span className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-xs font-semibold ${c.type === "company" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`} data-testid="provider-type">
                    <Briefcase className="h-3.5 w-3.5" />
                    {c.provider_label || (c.type === "company" ? "Cleaning Company" : "Independent Cleaner")}
                  </span>
                  <div className="flex items-center gap-4 mt-3">
                    <span className="inline-flex items-center gap-1 font-semibold"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {c.rating_avg || "New"}</span>
                    <span className="text-muted-foreground text-sm">{c.review_count} reviews</span>
                    <span className="text-muted-foreground text-sm">{c.completed_jobs} cleans</span>
                  </div>
                  <div className="mt-3"><BadgeRow earned={c.badges} max={4} /></div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-6">
                {c.verification?.insured && <Badge icon={ShieldCheck}>Fully Insured</Badge>}
                {c.verification?.identity && <Badge icon={CheckCircle2}>ID Verified</Badge>}
                {c.languages?.map((l) => <Badge key={l} icon={Globe}>{l}</Badge>)}
              </div>

              <div className="mt-4" data-testid="dbs-section"><DbsStatusBlock dbs={c.dbs} /></div>

              <p className="mt-6 leading-relaxed text-primary/80">{c.bio}</p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                {stats.map((s) => (
                  <div key={s.label} className="bg-secondary/40 rounded-xl p-4 text-center">
                    <p className="font-display text-xl font-semibold">{s.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* SERVICES */}
            <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8 mt-6">
              <h2 className="font-display text-xl font-semibold mb-4">Services offered</h2>
              <div className="flex flex-wrap gap-2">
                {c.services?.map((s) => <span key={s} className="px-3 py-1.5 rounded-full bg-secondary text-sm capitalize">{s.replace(/-/g, " ")}</span>)}
              </div>
            </div>

            {/* AVAILABILITY */}
            <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8 mt-6" data-testid="availability-section">
              <h2 className="font-display text-xl font-semibold mb-4">Availability</h2>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Available days</p>
                <div className="flex flex-wrap gap-2">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
                    const on = c.availability?.working_days?.includes(i);
                    return <span key={d} className={`px-3 py-1.5 rounded-full text-sm ${on ? "bg-accent/10 text-accent font-medium" : "bg-secondary/50 text-muted-foreground line-through"}`}>{d}</span>;
                  })}
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2">Times</p>
                <div className="flex flex-wrap gap-2">
                  {[["morning", "Morning · 8am–12pm"], ["afternoon", "Afternoon · 12pm–5pm"], ["evening", "Evening · 5pm–9pm"]].map(([k, label]) => {
                    const on = (c.availability?.time_slots || {})[k];
                    return on ? <span key={k} className="px-3 py-1.5 rounded-full bg-secondary text-sm">{label}</span> : null;
                  })}
                </div>
              </div>
            </div>

            {/* ACHIEVEMENTS */}
            <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8 mt-6 pb-8" data-testid="achievements-section">
              <h2 className="font-display text-xl font-semibold mb-1">Achievements</h2>
              <p className="text-sm text-muted-foreground mb-5">Earned automatically from verified platform activity.</p>
              <AchievementsGrid earned={c.badges} showLocked />
            </div>

            {/* GALLERY */}
            {c.gallery?.length > 0 && (
              <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8 mt-6">
                <h2 className="font-display text-xl font-semibold mb-4">Recent work</h2>
                <div className="grid grid-cols-3 gap-3">
                  {c.gallery.map((g, i) => <img key={i} src={g} alt="" className="rounded-xl h-28 sm:h-36 w-full object-cover" loading="lazy" />)}
                </div>
              </div>
            )}

            {/* REVIEWS */}
            <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8 mt-6" data-testid="reviews-section">
              <h2 className="font-display text-xl font-semibold mb-4">Reviews ({c.reviews?.length || 0})</h2>
              <div className="space-y-5">
                {c.reviews?.length ? c.reviews.map((r) => (
                  <div key={r.review_id} className="border-b border-border pb-5 last:border-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.customer_name}</span>
                      <span className="inline-flex items-center gap-1 text-sm"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {r.rating}</span>
                    </div>
                    <p className="text-muted-foreground mt-1">{r.text}</p>
                  </div>
                )) : <p className="text-muted-foreground">No reviews yet.</p>}
              </div>
            </div>
          </div>

          {/* BOOKING SIDEBAR */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="bg-card rounded-2xl border border-primary/5 p-6 shadow-lg">
              <div className="flex items-baseline gap-1">                <span className="font-display text-3xl font-semibold">£{c.hourly_rate}</span>
                <span className="text-muted-foreground">/hour</span>
                <span className="text-xs text-muted-foreground ml-1">standard</span>
              </div>
              {c.priority_enabled && (
                <div className="mt-2" data-testid="priority-rate-block">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold" data-testid="priority-available-badge">
                    <Zap className="h-3.5 w-3.5" /> Priority Bookings Available
                  </span>
                  <p className="text-sm mt-2">
                    <strong>£{c.priority_hourly_rate || c.hourly_rate}/hour</strong> <span className="text-muted-foreground">for urgent / last-minute cleans</span>
                  </p>
                  {c.priority_notes && <p className="text-xs text-muted-foreground mt-1">{c.priority_notes}</p>}
                </div>
              )}
              <p className="text-sm text-muted-foreground mt-2">Minimum {c.min_booking_hours} hours on hourly services</p>
              <div className="mt-4 space-y-1.5 text-sm" data-testid="profile-service-prices">
                {Object.entries(priceOpts || {}).map(([slug, o]) => {
                  const name = services.find((s) => s.slug === slug)?.name || slug;
                  const hourly = ["hourly", "hourly_or_quote", "instant_or_quote"].includes(o.model);
                  const label = o.quote_only ? "By quote"
                    : hourly ? `£${o.hourly_rate}/hour`
                      : o.choices?.length ? `from £${Math.min(...o.choices.map((x) => x.price)).toFixed(2)}`
                        : o.per_room ? `£${o.per_room}/room` : "By quote";
                  return (
                    <div key={slug} className="flex justify-between gap-3" data-testid={`profile-price-${slug}`}>
                      <span className="text-muted-foreground">{name}</span><span className="font-medium">{label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 p-3 rounded-xl bg-secondary/60 text-xs" data-testid="fee-transparency-note">
                <p className="font-semibold">What you'll pay</p>
                <p className="mt-1">
                  Cleaning price, {c.premium ? "no My Clean Connect™ service charge (Premium cleaner)" : "a £1.99 My Clean Connect™ service charge"} and
                  a small admin / payment fee. You'll see every line and the exact total before you
                  pay — nothing is added later at the payment stage.
                </p>
              </div>
              {c.confirmed_no_shows > 0 ? (
                <p className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold" data-testid="confirmed-no-shows">
                  <AlertTriangle className="h-3.5 w-3.5" /> {c.confirmed_no_shows} Confirmed No-Show{c.confirmed_no_shows === 1 ? "" : "s"}
                </p>
              ) : (
                <p className="mt-3 text-xs text-muted-foreground" data-testid="confirmed-no-shows">No-Shows: 0</p>
              )}
              <button onClick={() => navigate(`/book/${c.cleaner_id}`)} className="w-full mt-5 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors" data-testid="book-now-btn">
                Book now
              </button>
              <div className="flex gap-2 mt-3">
                <button onClick={toggleFav} className="flex-1 py-2.5 rounded-full border border-border font-medium hover:bg-secondary transition-colors inline-flex items-center justify-center gap-2" data-testid="favourite-btn">
                  <Heart className={`h-4 w-4 ${fav ? "fill-accent text-accent" : ""}`} /> Save
                </button>
                <button onClick={() => user ? navigate("/messages", { state: { to: c.cleaner_id, name: c.name } }) : navigate("/login")} className="flex-1 py-2.5 rounded-full border border-border font-medium hover:bg-secondary transition-colors inline-flex items-center justify-center gap-2" data-testid="message-btn">
                  <MessageSquare className="h-4 w-4" /> Chat
                </button>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-4 flex items-center justify-center gap-1">
                <Clock className="h-3 w-3" /> Not charged until the cleaner accepts
              </p>
            </div>
          </aside>
        </div>
      </div>
      <div className="mt-16"><Footer /></div>
    </div>
  );
}

function Badge({ icon: Icon, children }) {
  return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-sm"><Icon className="h-3.5 w-3.5 text-accent" /> {children}</span>;
}
